import { Team, User } from '@seatix/shared-domain-auth';

/**
 * Session fields propagated across apps via the `auth_transfer` param. Mirrors
 * the shape `withStorageSync` persists to `localStorage['auth']`, minus the
 * volatile `phone_verified_at`, keeping the URL compact while carrying enough
 * to hydrate `AuthStore` on the destination app.
 */
type AuthTransferUser = Omit<User, 'phone_verified_at'>;

function slimUser(user: User): AuthTransferUser {
  const {
    id,
    email,
    first_name,
    last_name,
    username,
    provider,
    provider_id,
    is_temporary,
    email_verified_at,
    created_at,
    updated_at,
    has_recent_activity,
    needs_email_verification,
    avatar_url,
    phone,
    roles,
    teams,
  } = user;
  return {
    id,
    email,
    first_name,
    last_name,
    username,
    provider,
    provider_id,
    is_temporary,
    email_verified_at,
    created_at,
    updated_at,
    has_recent_activity,
    needs_email_verification,
    avatar_url,
    phone,
    roles,
    teams,
  };
}

/**
 * Appends the current session as an `auth_transfer` query param to a cross-app
 * redirect URL, so the destination app hydrates `AuthStore` with THIS session —
 * overriding any different (stale) session already in its own localStorage.
 *
 * Write-side counterpart of {@link readAuthTransfer}, which the destination app
 * calls in main.ts before bootstrap. No-op when there is no authenticated user.
 */
export function appendAuthTransfer(
  url: string,
  user: User | null,
  currentTeam: Team | null,
): string {
  if (!user) return url;

  const payload = encodeURIComponent(
    JSON.stringify({ user: slimUser(user), current_team: currentTeam }),
  );
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}auth_transfer=${payload}`;
}

/**
 * Reads the `auth_transfer` query param written by another Seatix app and writes
 * it to `localStorage['auth']` so that `AuthStore` (via `withStorageSync`) can
 * hydrate with the correct user before any guard fires.
 *
 * Must be called BEFORE `bootstrapApplication` — in main.ts — to guarantee it
 * runs before AuthStore is ever constructed.
 */
export function readAuthTransfer(): void {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('auth_transfer');
    if (!raw) return;

    const decoded = JSON.parse(decodeURIComponent(raw));
    if (!decoded?.user) return;

    localStorage.setItem('auth', JSON.stringify(decoded));

    params.delete('auth_transfer');
    const newSearch = params.toString();
    const newUrl =
      window.location.pathname +
      (newSearch ? `?${newSearch}` : '') +
      window.location.hash;
    history.replaceState(null, '', newUrl);
  } catch {
    // silently swallow parse errors
  }
}
